import { alpha, Components, Theme } from '@mui/material/styles'

import colors from '@/assets/scss/_themes-vars.module.scss'

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    chip: true
    media: true
    neutral: true
  }
}

declare module '@mui/material/Chip/chipClasses' {
  interface ChipClasses {
    colorInfo: string
    colorError: string
    colorSuccess: string
  }
}

const components: Components<Theme> = {
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        borderRadius: '6px',
        textTransform: 'initial',
        fontSize: '14px',
        fontWeight: 500,
      },
      containedSecondary: {
        color: colors.yellow700,
      },
    },
    variants: [
      {
        props: { variant: 'media' },
        style: {
          backgroundColor: colors.paper,
          border: `1px solid ${colors.gray300}`,
          borderRadius: '6px',
          color: colors.indigo500,
          fontSize: '14px',
          fontWeight: 'bold',
          paddingTop: '5px',
          paddingBottom: '5px',
          ':hover': {
            backgroundColor: colors.indigo100,
          },
        },
      },
      {
        props: { variant: 'neutral' },
        style: {
          backgroundColor: colors.paper,
          border: `1px solid ${colors.gray300}`,
          borderRadius: '6px',
          paddingTop: '5px',
          paddingBottom: '5px',
          ':hover': {
            backgroundColor: colors.gray200,
          },
        },
      },
      {
        props: { variant: 'chip' },
        style: {
          backgroundColor: colors.indigo100,
          color: colors.indigo700,
          ':hover': {
            backgroundColor: colors.indigo100,
          },
          '&.MuiButton-chipSizeSmall': {
            padding: '4px 10px',
          },
        },
      },
      {
        props: { variant: 'chip', color: 'secondary' },
        style: {
          backgroundColor: colors.yellow100,
          color: colors.yellow700,
          ':hover': {
            backgroundColor: colors.yellow100,
          },
        },
      },
      {
        props: { variant: 'chip', color: 'info' },
        style: {
          backgroundColor: colors.blue100,
          color: colors.blue800,
          ':hover': {
            backgroundColor: colors.blue100,
          },
        },
      },
      {
        props: { variant: 'chip', color: 'error' },
        style: {
          backgroundColor: colors.red100,
          color: colors.red700,
          ':hover': {
            backgroundColor: colors.red100,
          },
        },
      },
      {
        props: { variant: 'chip', color: 'success' },
        style: {
          backgroundColor: colors.green100,
          color: colors.green700,
          ':hover': {
            backgroundColor: colors.green100,
          },
        },
      },
      {
        props: { variant: 'chip', color: 'warning' },
        style: {
          backgroundColor: colors.red100,
          color: colors.red700,
          ':hover': {
            backgroundColor: colors.red100,
          },
        },
      },
    ],
  },
  MuiChip: {
    styleOverrides: {
      colorPrimary: {
        backgroundColor: colors.indigo100,
        color: colors.indigo700,
      },
      colorSecondary: {
        backgroundColor: colors.yellow100,
        color: colors.yellow700,
      },
      colorInfo: {
        backgroundColor: colors.blue100,
        color: colors.blue800,
      },
      colorError: {
        backgroundColor: colors.red100,
        color: colors.red700,
      },
      colorSuccess: {
        backgroundColor: colors.green100,
        color: colors.green700,
      },
      filled: {
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '0 24px 16px',
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        position: 'absolute',
        top: '100%',
        whiteSpace: 'nowrap',
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        backgroundColor: '#fff',
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 180,
        color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
        boxShadow:
          'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
          padding: '4px 0',
        },
        '& .MuiMenuItem-root': {
          '& .MuiSvgIcon-root': {
            fontSize: 24,
            color: theme.palette.text.secondary,
            marginRight: theme.spacing(1.5),
          },
          '&:active': {
            backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
          },
        },
      }),
    },
  },
  MuiPaper: {
    styleOverrides: {
      elevation1: {
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
      },
    },
  },
}

export default components
